import { ArrowRight, BarChart3, Clapperboard, Fingerprint, Instagram, MonitorSmartphone, TrendingUp } from "lucide-react";
import Reveal from "@/components/Reveal";
import CardSlider from "@/components/CardSlider";
import CinematicButton from "@/components/CinematicButton";

const SERVICES = [
    {
        icon: BarChart3,
        title: "Data & Analytics",
        desc: "Turn raw data into valuable insights through professional analytics and visualization solutions.",
        items: ["Data Cleaning & Preparation", "Data Analysis", "Excel Dashboard Creation", "Power BI Dashboards", "Sales & Profit Reports", "Excel Automation"],
    },
    {
        icon: MonitorSmartphone,
        title: "Website Design",
        desc: "Professional websites designed to give businesses a strong online presence.",
        items: ["Business Websites", "E-commerce Websites", "Landing Pages", "Website Redesign", "Responsive Design"],
    },
    {
        icon: Fingerprint,
        title: "Logo & Branding",
        desc: "Build a memorable and professional identity for your business.",
        items: ["Logo Design", "Brand Identity", "Business Branding", "Social Media Kits"],
    },
    {
        icon: Clapperboard,
        title: "AI Video & Ads",
        desc: "Create engaging promotional content using modern AI-powered creative tools.",
        items: ["AI Video Creation", "Promotional Ads", "Product Videos", "Shorts & Reels"],
    },
    {
        icon: Instagram,
        title: "Social Media Creatives",
        desc: "Creative visual content for social media and digital promotion.",
        items: ["Instagram Posts & Reels", "YouTube Thumbnails", "WhatsApp Creatives", "Posters & Banners"],
    },
    {
        icon: TrendingUp,
        title: "Digital Marketing",
        desc: "Creative digital solutions to help businesses improve their online visibility.",
        items: ["Social Media Marketing", "Content Creation", "Campaign Creatives", "Growth Strategies"],
    },
];

export default function Services() {
    return (
        <section id="services" className="py-20 lg:py-28" data-testid="services-section">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <span className="h-px w-10 bg-gold-deep" />
                            <span className="text-[11px] font-semibold tracking-[0.34em] text-burgundy">OUR SERVICES</span>
                        </div>
                        <h2 className="text-4xl sm:text-5xl text-charcoal">What We Do</h2>
                        <p className="mt-3 font-serif italic text-xl sm:text-2xl text-burgundy">Services Designed for Your Growth</p>
                    </div>
                    <p className="max-w-md text-charcoal/70 md:text-right">
                        From data analytics and financial solutions to website design, branding, and digital creative services, we provide practical solutions to help your business grow.
                    </p>
                </Reveal>

                <CardSlider
                    testPrefix="services"
                    items={SERVICES.map((s, i) => (
                        <Reveal key={s.title} delay={(i % 3) * 0.08} className="h-full">
                            <div
                                className="group card-lift h-full bg-cream-soft border border-burgundy/10 rounded-2xl p-8 flex flex-col"
                                data-testid={`service-card-${i}`}
                            >
                                <span className="w-12 h-12 rounded-xl bg-olive/25 text-burgundy flex items-center justify-center transition-colors duration-300 group-hover:bg-burgundy group-hover:text-cream">
                                    <s.icon className="w-6 h-6" strokeWidth={1.6} />
                                </span>
                                <h3 className="mt-6 text-[22px] text-charcoal">{s.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-charcoal/65">{s.desc}</p>
                                <ul className="mt-5 space-y-2 border-t border-burgundy/10 pt-5">
                                    {s.items.map((item) => (
                                        <li key={item} className="flex items-start gap-2.5 text-sm text-charcoal/80">
                                            <span className="mt-[7px] w-1.5 h-1.5 rotate-45 bg-gold-deep shrink-0" aria-hidden="true" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                    ))}
                />

                <Reveal className="mt-14 text-center">
                    <CinematicButton href="#contact" className="btn btn-burgundy group" testid="services-discuss-cta">
                        Let's Discuss Your Project
                        <ArrowRight className="btn-arrow w-4 h-4" />
                    </CinematicButton>
                </Reveal>
            </div>
        </section>
    );
}
